import { create } from "zustand";
import {
  MaintenanceSchedule,
  MaintenanceLog,
  PreventiveMaintenanceTask,
  Project,
  ProjectApprovalLevel,
  ProjectPhase,
  InfectionControlChecklist,
} from "../types/api";
import {
  createMaintenanceSchedule,
  getAllMaintenanceSchedules,
  getMaintenanceSchedule,
  updateMaintenanceSchedule,
  deleteMaintenanceSchedule,
  searchMaintenanceSchedules,
  createMaintenanceLog,
  getAllMaintenanceLogs,
  getMaintenanceLog,
  updateMaintenanceLog,
  deleteMaintenanceLog,
  getLogsByAsset,
  getLogsByTask,
  getAllPreventiveMaintenanceTasks,
  getPreventiveMaintenanceTask,
  createProject,
  getAllProjects,
  getProject,
  updateProject,
  deleteProject,
  submitProjectForApproval,
  createProjectPhase,
  getAllProjectPhases,
  getProjectPhase,
  updateProjectPhase,
  deleteProjectPhase,
  getProjectPhasesByProject,
  createInfectionControlChecklist,
  getAllInfectionControlChecklists,
  getInfectionControlChecklist,
  updateInfectionControlChecklist,
  deleteInfectionControlChecklist,
  approveInfectionControlChecklist,
  getInfectionControlChecklistsByProject,
} from "../service/api";

// ========== PREVENTIVE MAINTENANCE STORE ==========
interface PreventiveMaintenanceState {
  // Maintenance Schedules
  maintenanceSchedules: MaintenanceSchedule[];
  schedulesLoading: boolean;
  schedulesError: string | null;
  currentSchedule: MaintenanceSchedule | null;

  // Maintenance Logs
  maintenanceLogs: MaintenanceLog[];
  logsLoading: boolean;
  logsError: string | null;
  currentLog: MaintenanceLog | null;

  // Preventive Maintenance Tasks
  maintenanceTasks: PreventiveMaintenanceTask[];
  tasksLoading: boolean;
  tasksError: string | null;
  currentTask: PreventiveMaintenanceTask | null;

  schedulesPagination: {
    page: number;
    limit: number;
    total: number;
    skip: number;
  };
  logsPagination: {
    page: number;
    limit: number;
    total: number;
    skip: number;
  };
  tasksPagination: {
    page: number;
    limit: number;
    total: number;
    skip: number;
  };

  // Actions - Maintenance Schedules
  fetchMaintenanceSchedules: (params?: any) => Promise<void>;
  fetchMaintenanceScheduleById: (id: string) => Promise<MaintenanceSchedule | null>;
  createMaintenanceSchedule: (data: Partial<MaintenanceSchedule>) => Promise<MaintenanceSchedule>;
  updateMaintenanceSchedule: (id: string, data: Partial<MaintenanceSchedule>) => Promise<MaintenanceSchedule>;
  deleteMaintenanceSchedule: (id: string) => Promise<void>;
  searchMaintenanceSchedules: (keyword: string) => Promise<void>;

  // Actions - Maintenance Logs
  fetchMaintenanceLogs: (params?: any) => Promise<void>;
  fetchMaintenanceLogById: (id: string) => Promise<MaintenanceLog | null>;
  createMaintenanceLog: (data: Partial<MaintenanceLog>) => Promise<MaintenanceLog>;
  updateMaintenanceLog: (id: string, data: Partial<MaintenanceLog>) => Promise<MaintenanceLog>;
  deleteMaintenanceLog: (id: string) => Promise<void>;
  fetchLogsByAsset: (assetId: string) => Promise<void>;
  fetchLogsByTask: (taskId: string) => Promise<void>;

  // Actions - Preventive Maintenance Tasks
  fetchMaintenanceTasks: (params?: any) => Promise<void>;
  fetchMaintenanceTaskById: (id: string) => Promise<void>;

  // Reset Actions
  resetSchedules: () => void;
  resetLogs: () => void;
  resetTasks: () => void;
  resetAll: () => void;
}

export const usePreventiveMaintenanceStore = create<PreventiveMaintenanceState>(
  (set, get) => ({
    // Initial state - Schedules
    maintenanceSchedules: [],
    schedulesLoading: false,
    schedulesError: null,
    currentSchedule: null,

    // Initial state - Logs
    maintenanceLogs: [],
    logsLoading: false,
    logsError: null,
    currentLog: null,

    // Initial state - Tasks
    maintenanceTasks: [],
    tasksLoading: false,
    tasksError: null,
    currentTask: null,

    // Pagination
    schedulesPagination: {
      page: 1,
      limit: 10,
      total: 0,
      skip: 0,
    },
    logsPagination: {
      page: 1,
      limit: 10,
      total: 0,
      skip: 0,
    },
    tasksPagination: {
      page: 1,
      limit: 10,
      total: 0,
      skip: 0,
    },

    // Maintenance Schedule Actions
    fetchMaintenanceSchedules: async (params = {}) => {
      set({ schedulesLoading: true, schedulesError: null });
      try {
        const response = await getAllMaintenanceSchedules(params);
        let schedules: MaintenanceSchedule[] = [];
        let pagination = get().schedulesPagination;

        if (response.data?.data) {
          if (response.data.data.results && Array.isArray(response.data.data.results)) {
            schedules = response.data.data.results as MaintenanceSchedule[];
            pagination = {
              page: Math.floor((response.data.data.skip || 0) / (response.data.data.limit || 10)) + 1,
              limit: response.data.data.limit || 10,
              total: response.data.data.total || 0,
              skip: response.data.data.skip || 0,
            };
          } else if (Array.isArray(response.data.data)) {
            schedules = response.data.data as MaintenanceSchedule[];
          }
        } else if (response.data?.message && Array.isArray(response.data.message)) {
          schedules = response.data.message as MaintenanceSchedule[];
        }

        set({
          maintenanceSchedules: schedules,
          schedulesPagination: pagination,
          schedulesLoading: false,
        });
      } catch (error: any) {
        set({
          schedulesError: error.message || "Failed to fetch maintenance schedules",
          schedulesLoading: false,
        });
        throw error;
      }
    },

    fetchMaintenanceScheduleById: async (id: string): Promise<MaintenanceSchedule | null> => {
      set({ schedulesLoading: true, schedulesError: null });
      try {
        const response = await getMaintenanceSchedule(id);
        const schedule = (response.data?.data || response.data?.message || null) as MaintenanceSchedule | null;
        set({
          currentSchedule: schedule,
          schedulesLoading: false,
        });
        return schedule;
      } catch (error: any) {
        set({
          schedulesError: error.message || "Failed to fetch maintenance schedule",
          schedulesLoading: false,
        });
        throw error;
      }
    },

    createMaintenanceSchedule: async (data: Partial<MaintenanceSchedule>) => {
      try {
        const response = await createMaintenanceSchedule(data);
        const schedule = response.data?.data || response.data?.message || data;
        set((state) => ({
          maintenanceSchedules: [schedule as MaintenanceSchedule, ...state.maintenanceSchedules],
        }));
        return schedule as MaintenanceSchedule;
      } catch (error: any) {
        throw error;
      }
    },

    updateMaintenanceSchedule: async (id: string, data: Partial<MaintenanceSchedule>) => {
      try {
        const response = await updateMaintenanceSchedule(id, data);
        const schedule = response.data?.data || response.data?.message || data;
        set((state) => ({
          maintenanceSchedules: state.maintenanceSchedules.map((s) =>
            (s.id === id || s._id === id) ? (schedule as MaintenanceSchedule) : s
          ),
          currentSchedule:
            state.currentSchedule?.id === id || state.currentSchedule?._id === id
              ? (schedule as MaintenanceSchedule)
              : state.currentSchedule,
        }));
        return schedule as MaintenanceSchedule;
      } catch (error: any) {
        throw error;
      }
    },

    deleteMaintenanceSchedule: async (id: string) => {
      try {
        await deleteMaintenanceSchedule(id);
        set((state) => ({
          maintenanceSchedules: state.maintenanceSchedules.filter(
            (s) => s.id !== id && s._id !== id
          ),
        }));
      } catch (error: any) {
        throw error;
      }
    },

    searchMaintenanceSchedules: async (keyword: string) => {
      set({ schedulesLoading: true, schedulesError: null });
      try {
        const response = await searchMaintenanceSchedules(keyword);
        let schedules: MaintenanceSchedule[] = [];

        if (response.data?.data) {
          if (Array.isArray(response.data.data)) {
            schedules = response.data.data as MaintenanceSchedule[];
          }
        } else if (response.data?.message && Array.isArray(response.data.message)) {
          schedules = response.data.message as MaintenanceSchedule[];
        }

        set({
          maintenanceSchedules: schedules,
          schedulesLoading: false,
        });
      } catch (error: any) {
        set({
          schedulesError: error.message || "Failed to search maintenance schedules",
          schedulesLoading: false,
        });
        throw error;
      }
    },

    // Maintenance Log Actions
    fetchMaintenanceLogs: async (params = {}) => {
      set({ logsLoading: true, logsError: null });
      try {
        const response = await getAllMaintenanceLogs(params);
        let logs: MaintenanceLog[] = [];
        let pagination = get().logsPagination;

        if (response.data?.data) {
          if (response.data.data.results && Array.isArray(response.data.data.results)) {
            logs = response.data.data.results as MaintenanceLog[];
            pagination = {
              page: Math.floor((response.data.data.skip || 0) / (response.data.data.limit || 10)) + 1,
              limit: response.data.data.limit || 10,
              total: response.data.data.total || 0,
              skip: response.data.data.skip || 0,
            };
          } else if (Array.isArray(response.data.data)) {
            logs = response.data.data as MaintenanceLog[];
          }
        } else if (response.data?.results && Array.isArray(response.data.results)) {
          logs = response.data.results as MaintenanceLog[];
        } else if (response.data?.message && Array.isArray(response.data.message)) {
          logs = response.data.message as MaintenanceLog[];
        }

        set({
          maintenanceLogs: logs,
          logsPagination: pagination,
          logsLoading: false,
        });
      } catch (error: any) {
        set({
          logsError: error.message || "Failed to fetch maintenance logs",
          logsLoading: false,
        });
        throw error;
      }
    },

    fetchMaintenanceLogById: async (id: string): Promise<MaintenanceLog | null> => {
      set({ logsLoading: true, logsError: null });
      try {
        const response = await getMaintenanceLog(id);
        const log = (response.data?.data || response.data?.message || null) as MaintenanceLog | null;
        set({
          currentLog: log,
          logsLoading: false,
        });
        return log;
      } catch (error: any) {
        set({
          logsError: error.message || "Failed to fetch maintenance log",
          logsLoading: false,
        });
        throw error;
      }
    },

    createMaintenanceLog: async (data: Partial<MaintenanceLog>) => {
      try {
        const response = await createMaintenanceLog(data);
        const log = response.data?.data || response.data?.message || data;
        set((state) => ({
          maintenanceLogs: [log as MaintenanceLog, ...state.maintenanceLogs],
        }));
        return log as MaintenanceLog;
      } catch (error: any) {
        throw error;
      }
    },

    updateMaintenanceLog: async (id: string, data: Partial<MaintenanceLog>) => {
      try {
        const response = await updateMaintenanceLog(id, data);
        const log = response.data?.data || response.data?.message || data;
        set((state) => ({
          maintenanceLogs: state.maintenanceLogs.map((l) =>
            (l.id === id || l._id === id) ? (log as MaintenanceLog) : l
          ),
          currentLog:
            state.currentLog?.id === id || state.currentLog?._id === id
              ? (log as MaintenanceLog)
              : state.currentLog,
        }));
        return log as MaintenanceLog;
      } catch (error: any) {
        throw error;
      }
    },

    deleteMaintenanceLog: async (id: string) => {
      try {
        await deleteMaintenanceLog(id);
        set((state) => ({
          maintenanceLogs: state.maintenanceLogs.filter(
            (l) => l.id !== id && l._id !== id
          ),
        }));
      } catch (error: any) {
        throw error;
      }
    },

    fetchLogsByAsset: async (assetId: string) => {
      set({ logsLoading: true, logsError: null });
      try {
        const response = await getLogsByAsset(assetId);
        let logs: MaintenanceLog[] = [];

        if (response.data?.data) {
          if (Array.isArray(response.data.data)) {
            logs = response.data.data as MaintenanceLog[];
          } else if (response.data.data.results && Array.isArray(response.data.data.results)) {
            logs = response.data.data.results as MaintenanceLog[];
          }
        } else if (response.data?.message && Array.isArray(response.data.message)) {
          logs = response.data.message as MaintenanceLog[];
        }

        set({
          maintenanceLogs: logs,
          logsLoading: false,
        });
      } catch (error: any) {
        set({
          logsError: error.message || "Failed to fetch logs by asset",
          logsLoading: false,
        });
        throw error;
      }
    },

    fetchLogsByTask: async (taskId: string) => {
      set({ logsLoading: true, logsError: null });
      try {
        const response = await getLogsByTask(taskId);
        let logs: MaintenanceLog[] = [];

        if (response.data?.data) {
          if (Array.isArray(response.data.data)) {
            logs = response.data.data as MaintenanceLog[];
          } else if (response.data.data.results && Array.isArray(response.data.data.results)) {
            logs = response.data.data.results as MaintenanceLog[];
          }
        } else if (response.data?.message && Array.isArray(response.data.message)) {
          logs = response.data.message as MaintenanceLog[];
        }

        set({
          maintenanceLogs: logs,
          logsLoading: false,
        });
      } catch (error: any) {
        set({
          logsError: error.message || "Failed to fetch logs by task",
          logsLoading: false,
        });
        throw error;
      }
    },

    // Preventive Maintenance Task Actions
    fetchMaintenanceTasks: async (params = {}) => {
      set({ tasksLoading: true, tasksError: null });
      try {
        const response = await getAllPreventiveMaintenanceTasks(params);
        let tasks: PreventiveMaintenanceTask[] = [];
        let pagination = get().tasksPagination;

        if (response.data?.data) {
          if (response.data.data.results && Array.isArray(response.data.data.results)) {
            tasks = response.data.data.results as PreventiveMaintenanceTask[];
            pagination = {
              page: Math.floor((response.data.data.skip || 0) / (response.data.data.limit || 10)) + 1,
              limit: response.data.data.limit || 10,
              total: response.data.data.total || 0,
              skip: response.data.data.skip || 0,
            };
          } else if (Array.isArray(response.data.data)) {
            tasks = response.data.data as PreventiveMaintenanceTask[];
          }
        } else if (response.data?.results && Array.isArray(response.data.results)) {
          tasks = response.data.results as PreventiveMaintenanceTask[];
        } else if (response.data?.message && Array.isArray(response.data.message)) {
          tasks = response.data.message as PreventiveMaintenanceTask[];
        }

        set({
          maintenanceTasks: tasks,
          tasksPagination: pagination,
          tasksLoading: false,
        });
      } catch (error: any) {
        set({
          tasksError: error.message || "Failed to fetch maintenance tasks",
          tasksLoading: false,
        });
        throw error;
      }
    },

    fetchMaintenanceTaskById: async (id: string) => {
      set({ tasksLoading: true, tasksError: null });
      try {
        const response = await getPreventiveMaintenanceTask(id);
        const task = response.data?.data || response.data?.message || null;
        set({
          currentTask: task,
          tasksLoading: false,
        });
        return task;
      } catch (error: any) {
        set({
          tasksError: error.message || "Failed to fetch maintenance task",
          tasksLoading: false,
        });
        throw error;
      }
    },

    // Reset Actions
    resetSchedules: () => {
      set({
        maintenanceSchedules: [],
        schedulesLoading: false,
        schedulesError: null,
        currentSchedule: null,
        schedulesPagination: {
          page: 1,
          limit: 10,
          total: 0,
          skip: 0,
        },
      });
    },

    resetLogs: () => {
      set({
        maintenanceLogs: [],
        logsLoading: false,
        logsError: null,
        currentLog: null,
        logsPagination: {
          page: 1,
          limit: 10,
          total: 0,
          skip: 0,
        },
      });
    },

    resetTasks: () => {
      set({
        maintenanceTasks: [],
        tasksLoading: false,
        tasksError: null,
        currentTask: null,
        tasksPagination: {
          page: 1,
          limit: 10,
          total: 0,
          skip: 0,
        },
      });
    },

    resetAll: () => {
      set({
        maintenanceSchedules: [],
        schedulesLoading: false,
        schedulesError: null,
        currentSchedule: null,
        maintenanceLogs: [],
        logsLoading: false,
        logsError: null,
        currentLog: null,
        maintenanceTasks: [],
        tasksLoading: false,
        tasksError: null,
        currentTask: null,
        schedulesPagination: {
          page: 1,
          limit: 10,
          total: 0,
          skip: 0,
        },
        logsPagination: {
          page: 1,
          limit: 10,
          total: 0,
          skip: 0,
        },
        tasksPagination: {
          page: 1,
          limit: 10,
          total: 0,
          skip: 0,
        },
      });
    },
  })
);

// ========== PROJECT PLANNING & APPROVALS STORE ==========
interface ProjectUtilityState {
  // Projects
  projects: Project[];
  projectsLoading: boolean;
  projectsError: string | null;
  currentProject: Project | null;
  projectsPagination: {
    page: number;
    limit: number;
    total: number;
    skip: number;
  };

  // Actions
  fetchProjects: (params?: any) => Promise<void>;
  fetchProjectById: (id: string) => Promise<Project | null>;
  createProject: (data: Partial<Project>) => Promise<Project>;
  updateProject: (id: string, data: Partial<Project>) => Promise<Project>;
  deleteProject: (id: string) => Promise<void>;
  submitProjectForApproval: (id: string, approvalLevels: ProjectApprovalLevel[]) => Promise<void>;

  // Reset Actions
  resetProjects: () => void;
}

export const useProjectUtilityStore = create<ProjectUtilityState>(
  (set, get) => ({
    // Initial state
    projects: [],
    projectsLoading: false,
    projectsError: null,
    currentProject: null,
    projectsPagination: {
      page: 1,
      limit: 10,
      total: 0,
      skip: 0,
    },

    // Project Actions
    fetchProjects: async (params = {}) => {
      set({ projectsLoading: true, projectsError: null });
      try {
        const response = await getAllProjects(params);
        let projects: Project[] = [];
        let pagination = get().projectsPagination;

        if (response.data?.data) {
          if (response.data.data.results && Array.isArray(response.data.data.results)) {
            projects = response.data.data.results as Project[];
            pagination = {
              page: Math.floor((response.data.data.skip || 0) / (response.data.data.limit || 10)) + 1,
              limit: response.data.data.limit || 10,
              total: response.data.data.total || 0,
              skip: response.data.data.skip || 0,
            };
          } else if (Array.isArray(response.data.data)) {
            projects = response.data.data as Project[];
          }
        } else if (response.data?.message && Array.isArray(response.data.message)) {
          projects = response.data.message as Project[];
        }

        set({
          projects,
          projectsPagination: pagination,
          projectsLoading: false,
        });
      } catch (error: any) {
        set({
          projectsError: error.message || "Failed to fetch projects",
          projectsLoading: false,
        });
        throw error;
      }
    },

    fetchProjectById: async (id: string): Promise<Project | null> => {
      set({ projectsLoading: true, projectsError: null });
      try {
        const response = await getProject(id);
        const project = (response.data?.data || response.data?.message || null) as Project | null;
        set({
          currentProject: project,
          projectsLoading: false,
        });
        return project;
      } catch (error: any) {
        set({
          projectsError: error.message || "Failed to fetch project",
          projectsLoading: false,
        });
        throw error;
      }
    },

    createProject: async (data: Partial<Project>) => {
      set({ projectsLoading: true, projectsError: null });
      try {
        const response = await createProject(data);
        const project = response.data?.data || response.data?.message || data;
        set((state) => ({
          projects: [project as Project, ...state.projects],
          projectsLoading: false,
        }));
        return project as Project;
      } catch (error: any) {
        set({
          projectsError: error.message || "Failed to create project",
          projectsLoading: false,
        });
        throw error;
      }
    },

    updateProject: async (id: string, data: Partial<Project>) => {
      set({ projectsLoading: true, projectsError: null });
      try {
        const response = await updateProject(id, data);
        const project = response.data?.data || response.data?.message || data;
        set((state) => ({
          projects: state.projects.map((p) =>
            (p.id === id || p._id === id) ? (project as Project) : p
          ),
          currentProject:
            state.currentProject?.id === id || state.currentProject?._id === id
              ? (project as Project)
              : state.currentProject,
          projectsLoading: false,
        }));
        return project as Project;
      } catch (error: any) {
        set({
          projectsError: error.message || "Failed to update project",
          projectsLoading: false,
        });
        throw error;
      }
    },

    deleteProject: async (id: string) => {
      set({ projectsLoading: true, projectsError: null });
      try {
        await deleteProject(id);
        set((state) => ({
          projects: state.projects.filter(
            (p) => p.id !== id && p._id !== id
          ),
          projectsLoading: false,
        }));
      } catch (error: any) {
        set({
          projectsError: error.message || "Failed to delete project",
          projectsLoading: false,
        });
        throw error;
      }
    },

    submitProjectForApproval: async (id: string, approvalLevels: ProjectApprovalLevel[]) => {
      set({ projectsLoading: true, projectsError: null });
      try {
        await submitProjectForApproval(id, approvalLevels);
        set((state) => ({
          projects: state.projects.map((p) =>
            (p.id === id || p._id === id) ? { ...p, status: "Pending Approval" as Project["status"] } : p
          ),
          currentProject:
            state.currentProject?.id === id || state.currentProject?._id === id
              ? { ...state.currentProject, status: "Pending Approval" as Project["status"] }
              : state.currentProject as Project | null,
          projectsLoading: false,
        }));
      } catch (error: any) {
        set({
          projectsError: error.message || "Failed to submit project for approval",
          projectsLoading: false,
        });
        throw error;
      }
    },

    // Reset Actions
    resetProjects: () => {
      set({
        projects: [],
        projectsLoading: false,
        projectsError: null,
        currentProject: null,
        projectsPagination: {
          page: 1,
          limit: 10,
          total: 0,
          skip: 0,
        },
      });
    },
  })
);

// ========== INFECTION CONTROL & SAFETY PLANNING STORE ==========
interface InfectionControlState {
  // Checklists
  checklists: InfectionControlChecklist[];
  checklistsLoading: boolean;
  checklistsError: string | null;
  currentChecklist: InfectionControlChecklist | null;
  checklistsPagination: {
    page: number;
    limit: number;
    total: number;
    skip: number;
  };

  fetchChecklists: (params?: any) => Promise<void>;
  fetchChecklistById: (id: string) => Promise<InfectionControlChecklist | null>;
  createChecklist: (data: Partial<InfectionControlChecklist>) => Promise<InfectionControlChecklist>;
  updateChecklist: (id: string, data: Partial<InfectionControlChecklist>) => Promise<InfectionControlChecklist>;
  deleteChecklist: (id: string) => Promise<void>;
  approveChecklist: (id: string, remarks?: string) => Promise<void>;
  fetchChecklistsByProject: (projectId: string) => Promise<InfectionControlChecklist[]>;

    resetChecklists: () => void;
  }
  
export const useInfectionControlStore = create<InfectionControlState>(
  (set, get) => ({
    checklists: [],
    checklistsLoading: false,
    checklistsError: null,
    currentChecklist: null,
    checklistsPagination: {
      page: 1,
      limit: 10,
      total: 0,
      skip: 0,
    },

    fetchChecklists: async (params = {}) => {
      set({ checklistsLoading: true, checklistsError: null });
      try {
        const response = await getAllInfectionControlChecklists(params);
        let checklists: InfectionControlChecklist[] = [];
        let pagination = get().checklistsPagination;

        if (response.data?.data) {
          if (
            typeof response.data.data === "object" &&
            response.data.data !== null &&
            "results" in response.data.data &&
            Array.isArray((response.data.data as any).results)
          ) {
            const dataObj = response.data.data as {
              results: InfectionControlChecklist[];
              skip?: number;
              limit?: number;
              total?: number;
            };
            checklists = dataObj.results;
            pagination = {
              page: Math.floor((dataObj.skip || 0) / (dataObj.limit || 10)) + 1,
              limit: dataObj.limit || 10,
              total: dataObj.total || 0,
              skip: dataObj.skip || 0,
            };
          } else if (Array.isArray(response.data.data)) {
            checklists = response.data.data as InfectionControlChecklist[];
          }
        } else if (response.data?.message && Array.isArray(response.data.message)) {
          checklists = response.data.message as InfectionControlChecklist[];
        }

        set({
          checklists,
          checklistsPagination: pagination,
          checklistsLoading: false,
        });
      } catch (error: any) {
        set({
          checklistsError: error.message || "Failed to fetch checklists",
          checklistsLoading: false,
        });
        throw error;
      }
    },

    fetchChecklistById: async (id: string): Promise<InfectionControlChecklist | null> => {
      set({ checklistsLoading: true, checklistsError: null });
      try {
        const response = await getInfectionControlChecklist(id);
        const checklist = (response.data?.data || response.data?.message || null) as InfectionControlChecklist | null;
        set({
          currentChecklist: checklist,
          checklistsLoading: false,
        });
        return checklist;
      } catch (error: any) {
        set({
          checklistsError: error.message || "Failed to fetch checklist",
          checklistsLoading: false,
        });
        throw error;
      }
    },

    createChecklist: async (data: Partial<InfectionControlChecklist>) => {
      try {
        const response = await createInfectionControlChecklist(data);
        const checklist = response.data?.data || response.data?.message || data;
        set((state) => ({
          checklists: [checklist as InfectionControlChecklist, ...state.checklists],
        }));
        return checklist as InfectionControlChecklist;
      } catch (error: any) {
        throw error;
      }
    },

    updateChecklist: async (id: string, data: Partial<InfectionControlChecklist>) => {
      try {
        const response = await updateInfectionControlChecklist(id, data);
        const checklist = response.data?.data || response.data?.message || data;
        set((state) => ({
          checklists: state.checklists.map((c) =>
            (c.id === id || c._id === id) ? (checklist as InfectionControlChecklist) : c
          ),
          currentChecklist:
            state.currentChecklist?.id === id || state.currentChecklist?._id === id
              ? (checklist as InfectionControlChecklist)
              : state.currentChecklist,
        }));
        return checklist as InfectionControlChecklist;
      } catch (error: any) {
        throw error;
      }
    },

    deleteChecklist: async (id: string) => {
      try {
        await deleteInfectionControlChecklist(id);
        set((state) => ({
          checklists: state.checklists.filter(
            (c) => c.id !== id && c._id !== id
          ),
        }));
      } catch (error: any) {
        throw error;
      }
    },

    approveChecklist: async (id: string, remarks?: string) => {
      set({ checklistsLoading: true, checklistsError: null });
      try {
        await approveInfectionControlChecklist(id, remarks);
        set((state) => ({
          checklists: state.checklists.map((c) =>
            (c.id === id || c._id === id) ? { ...c, isApproved: true } : c
          ),
          currentChecklist:
            state.currentChecklist?.id === id || state.currentChecklist?._id === id
              ? { ...state.currentChecklist, isApproved: true }
              : state.currentChecklist,
          checklistsLoading: false,
        }));
      } catch (error: any) {
        set({
          checklistsError: error.message || "Failed to approve checklist",
          checklistsLoading: false,
        });
        throw error;
      }
    },

    fetchChecklistsByProject: async (projectId: string): Promise<InfectionControlChecklist[]> => {
      set({ checklistsLoading: true, checklistsError: null });
      try {
        const response = await getInfectionControlChecklistsByProject(projectId);
        const checklists = (response.data?.data || response.data?.message || []) as InfectionControlChecklist[];
        set({ checklistsLoading: false });
        return checklists;
      } catch (error: any) {
        set({
          checklistsError: error.message || "Failed to fetch checklists by project",
          checklistsLoading: false,
        });
        throw error;
      }
    },

    resetChecklists: () => {
      set({
        checklists: [],
        checklistsLoading: false,
        checklistsError: null,
        currentChecklist: null,
        checklistsPagination: {
          page: 1,
          limit: 10,
          total: 0,
          skip: 0,
        },
      });
    },
  })
);

// ========== BUDGETING & COST CONTROL STORE ==========
interface ProjectPhaseState {
    phases: ProjectPhase[];
    phasesLoading: boolean;
    phasesError: string | null;
    currentPhase: ProjectPhase | null;
    phasesPagination: {
      page: number;
      limit: number;
      total: number;
      skip: number;
    };
  
    fetchPhases: (params?: any) => Promise<void>;
    fetchPhaseById: (id: string) => Promise<ProjectPhase | null>;
    createPhase: (data: Partial<ProjectPhase>) => Promise<ProjectPhase>;
    updatePhase: (id: string, data: Partial<ProjectPhase>) => Promise<ProjectPhase>;
    deletePhase: (id: string) => Promise<void>;
    fetchPhasesByProject: (projectId: string) => Promise<ProjectPhase[]>;
  
    resetPhases: () => void;
  }
  
  export const useProjectPhaseStore = create<ProjectPhaseState>(
    (set, get) => ({
      phases: [],
      phasesLoading: false,
      phasesError: null,
      currentPhase: null,
      phasesPagination: {
        page: 1,
        limit: 10,
        total: 0,
        skip: 0,
      },
  
      fetchPhases: async (params = {}) => {
        set({ phasesLoading: true, phasesError: null });
        try {
          const response = await getAllProjectPhases(params);
          let phases: ProjectPhase[] = [];
          let pagination = get().phasesPagination;
  
          if (response.data?.data) {
            if (
            typeof response.data.data === "object" &&
            response.data.data !== null &&
            "results" in response.data.data &&
            Array.isArray((response.data.data as any).results)
          ) {
            const dataObj = response.data.data as {
              results: ProjectPhase[];
              skip?: number;
              limit?: number;
              total?: number;
            };
            phases = dataObj.results;
            pagination = {
              page: Math.floor((dataObj.skip || 0) / (dataObj.limit || 10)) + 1,
              limit: dataObj.limit || 10,
              total: dataObj.total || 0,
              skip: dataObj.skip || 0,
            };
          } else if (Array.isArray(response.data.data)) {
              phases = response.data.data as ProjectPhase[];
            }
          } else if (response.data?.message && Array.isArray(response.data.message)) {
            phases = response.data.message as ProjectPhase[];
          }
  
          set({
            phases,
            phasesPagination: pagination,
            phasesLoading: false,
          });
        } catch (error: any) {
          set({
            phasesError: error.message || "Failed to fetch project phases",
            phasesLoading: false,
          });
          throw error;
        }
      },
  
      fetchPhaseById: async (id: string): Promise<ProjectPhase | null> => {
        set({ phasesLoading: true, phasesError: null });
        try {
          const response = await getProjectPhase(id);
          const phase = (response.data?.data || response.data?.message || null) as ProjectPhase | null;
          set({
            currentPhase: phase,
            phasesLoading: false,
          });
          return phase;
        } catch (error: any) {
          set({
            phasesError: error.message || "Failed to fetch project phase",
            phasesLoading: false,
          });
          throw error;
        }
      },
  
      createPhase: async (data: Partial<ProjectPhase>) => {
        set({ phasesLoading: true, phasesError: null });
        try {
          const response = await createProjectPhase(data);
          const phase = response.data?.data || response.data?.message || data;
          set((state) => ({
            phases: [phase as ProjectPhase, ...state.phases],
            phasesLoading: false,
          }));
          return phase as ProjectPhase;
        } catch (error: any) {
          set({
            phasesError: error.message || "Failed to create project phase",
            phasesLoading: false,
          });
          throw error;
        }
      },
  
      updatePhase: async (id: string, data: Partial<ProjectPhase>) => {
        set({ phasesLoading: true, phasesError: null });
        try {
          const response = await updateProjectPhase(id, data);
          const phase = response.data?.data || response.data?.message || data;
          set((state) => ({
            phases: state.phases.map((p) =>
              (p.id === id || p._id === id) ? (phase as ProjectPhase) : p
            ),
            currentPhase:
              state.currentPhase?.id === id || state.currentPhase?._id === id
                ? (phase as ProjectPhase)
                : state.currentPhase,
            phasesLoading: false,
          }));
          return phase as ProjectPhase;
        } catch (error: any) {
          set({
            phasesError: error.message || "Failed to update project phase",
            phasesLoading: false,
          });
          throw error;
        }
      },
  
      deletePhase: async (id: string) => {
        set({ phasesLoading: true, phasesError: null });
        try {
          await deleteProjectPhase(id);
          set((state) => ({
            phases: state.phases.filter(
              (p) => p.id !== id && p._id !== id
            ),
            phasesLoading: false,
          }));
        } catch (error: any) {
          set({
            phasesError: error.message || "Failed to delete project phase",
            phasesLoading: false,
          });
          throw error;
        }
      },
  
      fetchPhasesByProject: async (projectId: string): Promise<ProjectPhase[]> => {
        set({ phasesLoading: true, phasesError: null });
        try {
          const response = await getProjectPhasesByProject(projectId);
          const phases = (response.data?.data || response.data?.message || []) as ProjectPhase[];
          set({ phasesLoading: false });
          return phases;
        } catch (error: any) {
          set({
            phasesError: error.message || "Failed to fetch phases by project",
            phasesLoading: false,
          });
          throw error;
        }
      },
  
      resetPhases: () => {
        set({
          phases: [],
          phasesLoading: false,
          phasesError: null,
          currentPhase: null,
          phasesPagination: {
            page: 1,
            limit: 10,
            total: 0,
            skip: 0,
          },
        });
      },
    })
  );
